<script lang="ts">
  import { personIcon } from '@nrk/core-icons';
  import { nrkLogo } from '@nrk/core-icons/logo';
  import { startWith } from 'rxjs';
  import type { Snippet } from 'svelte';
  import { gameInfo$ } from '../api/gameInfo';
  import logo from '../assets/logo.webp';
  import LoginAgainModal from '../components/LoginAgainModal.svelte';
  import Toasts from '../components/Toasts.svelte';
  import { loginWithMicrosoft, logout, user } from '../firebase/auth';
  import BildebankenHost from '../lib/bildebanken/BildebankenHost.svelte';
  import Icon from '../lib/Icon.svelte';
  import LockBadge from '../lib/LockBadge.svelte';
  import ThemeToggle from '../lib/ThemeToggle.svelte';
  import { p } from '../utils/router';
  import { useClock } from '../utils/useClock.svelte';

  interface Props {
    children: Snippet;
  }
  let { children }: Props = $props();

  let logoutDialog: HTMLDialogElement | undefined = $state();
  const gameInfo = gameInfo$.pipe(startWith(null));
  const clock = useClock();

  const isLocked = $derived.by(() => {
    const lockedAt = $gameInfo?.state.lockedAt;
    if (lockedAt == null) return false;
    return clock.now >= lockedAt;
  });

  const hasCountdown = $derived.by(() => {
    const lockedAt = $gameInfo?.state.lockedAt;
    if (lockedAt == null) return false;
    return clock.now < lockedAt;
  });
</script>

<Toasts />
<BildebankenHost />

<header>
  <div class="bar">
    <a class="brand" href={p('/')}>
      <Icon svg={nrkLogo} label="NRK" />
      <img src={logo} alt="" />
    </a>

    <div class="row account">
      <ThemeToggle />
      {#if $user == null}
        <button class="btn secondary" onclick={loginWithMicrosoft}>
          Logg på
        </button>
      {:else}
        <button class="btn clear" onclick={() => logoutDialog?.showModal()}>
          <Icon svg={personIcon} />
          <span class="name">{$user.displayName}</span>
        </button>
      {/if}
    </div>
  </div>
</header>

<dialog bind:this={logoutDialog} class="confirm" aria-labelledby="logout-title">
  <h2 id="logout-title">Logg av?</h2>
  <p>Du blir logget av Maskorama Admin.</p>
  <form method="dialog" class="actions">
    <button class="btn secondary" value="cancel">Avbryt</button>
    <button class="btn primary danger" value="confirm" onclick={logout}
      >Logg av</button
    >
  </form>
</dialog>

<LoginAgainModal />

<main
  id="main"
  tabindex="-1"
  class:locked={isLocked}
  class:locking={hasCountdown}
  class:open={!isLocked && !hasCountdown}
>
  {#if $user != null}
    <div class="page">
      <div class="title-row">
        <h1>Maskorama Admin</h1>
        <LockBadge />
      </div>
      {@render children()}
    </div>
  {:else}
    <div class="page welcome stack">
      <h1>Maskorama Admin</h1>
      <p>Logg på for å administrere deltakere, spillinfo og gruppering.</p>
      <button class="btn primary" onclick={loginWithMicrosoft}>Logg på</button>
    </div>
  {/if}
</main>

<style>
  header {
    position: sticky;
    inset-block-start: 0;
    z-index: 5;
    background: var(--brand-gradient);

    /* The bar is dark in BOTH themes, so every light-dark() token inside it
     * has to resolve to its dark value — otherwise the theme toggle turns
     * white-on-pink in light mode. One declaration does the whole subtree. */
    color-scheme: dark;
    color: var(--text);
  }

  img {
    height: var(--space-6);
  }

  .bar {
    display: flex;
    align-items: center;
    gap: var(--space-6);
    min-block-size: var(--header-height);
    max-inline-size: var(--page-width);
    margin-inline: auto;
    padding-inline: var(--space-6);
  }

  .brand {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    color: var(--text);
    font-weight: var(--nrk-sans-weight-bold);
    text-decoration: none;

    /* The logo ships with a hardcoded fill="#fff", which is invisible on a
     * light surface. Override it so it follows the text colour instead. */
    & :global(svg path) {
      fill: currentColor;
    }
  }

  .account {
    margin-inline-start: auto;
  }

  .confirm {
    max-inline-size: var(--nrk-dialog-width);
    margin: auto;
    padding: var(--space-6);
    border: 1px solid var(--border-strong);
    border-radius: var(--nrk-dialog-radius);
    background: var(--surface);
    color: var(--text);
    box-shadow: var(--shadow-md);

    &::backdrop {
      background: var(--nrk-dialog-color-backdrop);
    }
  }

  .confirm h2 {
    font-size: var(--text-lg);
  }

  .confirm p {
    margin-block: var(--space-2) var(--space-6);
    color: var(--text-muted);
  }

  .confirm .actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-2);
  }

  main {
    min-block-size: calc(100vh - var(--header-height));
    min-block-size: calc(100dvh - var(--header-height));
    transition: box-shadow var(--duration-slow) var(--ease);

    /* Programmatic focus target for the skip link; never in the tab order. */
    &:focus {
      outline: none;
    }

    /* Subtle glow around edges (below header) indicating round state */
    &.open {
      box-shadow: inset 0 0 24px -10px var(--success);
    }

    &.locking {
      box-shadow: inset 0 0 24px -10px var(--warning);
    }

    &.locked {
      box-shadow: inset 0 0 24px -10px var(--danger);
    }
  }

  .title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-2) var(--space-4);
    margin-block-end: var(--space-6);
  }

  .title-row h1 {
    margin: 0;
  }

  .welcome {
    --stack-gap: var(--space-6);
    align-items: start;
    max-inline-size: 40rem;
    padding-block: var(--space-16);
  }

  .welcome p {
    color: var(--text-muted);
    font-size: var(--text-lg);
  }

  @media (width < 48rem) {
    .bar {
      flex-wrap: wrap;
      padding-block: var(--space-3);
      padding-inline: var(--space-4);
      gap: var(--space-3);
    }
  }
</style>
